import React, { useState, ReactNode } from 'react';
import { makeStyles } from '~/UIKit/Style';
import classNames from 'classnames';
import {
  HEADER_HEIGHT_DESKTOP,
  HEADER_HEIGHT_DESKTOP_WITH_GLOBAL_ALERT,
  HEADER_HEIGHT_MOBILE,
  HEADER_HEIGHT_MOBILE_WITH_GLOBAL_ALERT,
} from '~/Constants/UI.constants';
import { PRIMARY_BLUE } from '~/Constants/Colors.constants';
import { ClaimManagerModal } from '~/Components';
import { motion } from 'framer-motion';
import { Container } from '@mui/material';
import { PrivateLayoutTitle, IPrivateLayoutTitleWithAction } from '../';
import { useLanguage, useUser } from '~/Hooks';
import { JumpingLoader } from '~/UIKit';
import { PrivateLayoutOptions } from '../PrivateLayout';
import { Alert } from '~/UIKit/Alert';
import { useSelector } from 'react-redux';
import { selectCurrentWarning } from '~/store/app/warning';
const useStyles = ({
  backgroundColor,
  hasGlobalWarning: hasGlobalWarning,
}: {
  backgroundColor: string;
  hasGlobalWarning: boolean;
}) =>
  makeStyles((theme) => {
    const desktopHeightSize = hasGlobalWarning
      ? HEADER_HEIGHT_DESKTOP_WITH_GLOBAL_ALERT
      : HEADER_HEIGHT_DESKTOP;
    const mobileHeightSize = hasGlobalWarning
      ? HEADER_HEIGHT_MOBILE_WITH_GLOBAL_ALERT
      : HEADER_HEIGHT_MOBILE;

    return {
      root: {
        display: 'flex',
        flexDirection: 'column',
      },
      appContent: {
        display: 'flex',
        flexDirection: 'row',
        height: `calc(100vh - ${mobileHeightSize}px - 3px)`,

        [theme.breakpoints.up('sm')]: {
          height: `calc(100vh - ${desktopHeightSize}px - 1px)`,
        },
        overflowY: 'auto',
        paddingLeft: 0,
        [theme.breakpoints.up('lg')]: {
          paddingLeft: 280,
        },
      },
      content: {
        flexGrow: 1,
        height: 'fit-content',
        minHeight: '100%',
        width: '100vw',
        backgroundColor,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        [theme.breakpoints.up('md')]: {
          overflowY: 'auto',
          padding: '0 15px',
        },
      },
      padding: {
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(3),
        },
      },
      container: {
        position: 'relative',
        padding: 0,
        maxWidth: '1100px',
      },
    };
  });

interface PrivateLayoutDetailsProps {
  children: ReactNode;
  backgroundColor?: string;
  title?: string | IPrivateLayoutTitleWithAction;
  actionName?: string;
  onActionTaken?: () => void;
  actionDisabled?: boolean;
  backRoute?: string;
  hideBackButton?: boolean;
  cancelInsteadOfBack?: boolean;
  options?: PrivateLayoutOptions[];
  isNew?: boolean;
  articleLink?: string;
  secondaryTitleActionElement?: JSX.Element | null;
  disableMobile?: boolean;
}

const variants = {
  enter: {
    opacity: 0,
  },
  center: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
  },
};

export const PrivateLayoutDetails = ({
  children,
  backgroundColor = PRIMARY_BLUE,
  title,
  actionName,
  onActionTaken,
  actionDisabled,
  backRoute,
  hideBackButton,
  cancelInsteadOfBack,
  options,
  isNew,
  articleLink,
  secondaryTitleActionElement,
  disableMobile = false,
}: PrivateLayoutDetailsProps) => {
  const hasGlobalWarning = useSelector(selectCurrentWarning);
  const { role, isCurrentUserLoading } = useUser();
  const { t } = useLanguage();

  const { classes } = useStyles({
    backgroundColor,
    hasGlobalWarning,
  })();
  const [hasAccess, setHasAccess] = useState(!!role);
  if (isCurrentUserLoading) {
    return <JumpingLoader />;
  }

  if (!hasAccess) {
    return <ClaimManagerModal onRightsGranted={setHasAccess} />;
  }

  return (
    <div>
      {isNew && articleLink && (
        <Alert id={`${title}`} message={t('feature.description.new', { link: t(articleLink) })} />
      )}
      <div className={classes.appContent} id="private_layout_details">
        <main className={classNames({ [classes.content]: true })}>
          {(!!title || !!secondaryTitleActionElement) && (
            <PrivateLayoutTitle
              title={title}
              backRoute={backRoute}
              actionName={actionName}
              onActionTaken={onActionTaken}
              actionDisabled={actionDisabled}
              hideBackButton={hideBackButton}
              cancelInsteadOfBack={cancelInsteadOfBack}
              options={options}
              secondaryAction={secondaryTitleActionElement}
              disableMobile={disableMobile}
            />
          )}
          <motion.div
            animate="center"
            className={classes.root}
            custom={{ direction: 1 }}
            exit="exit"
            initial="enter"
            variants={variants}
          >
            <Container className={classes.container}>
              <>{children}</>
            </Container>
          </motion.div>
        </main>
      </div>
    </div>
  );
};
